import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Fragment, useEffect, useState, useCallback } from "react";
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import { computed, useSignal } from "@preact/signals-react";
import { BsBank, BsBuilding } from "react-icons/bs";
import { LuBadgeDollarSign } from "react-icons/lu";
import { TbLetterA, TbLetterV, TbCheck, TbStar, TbMapPin, TbPhone, TbFileText, TbWritingSign } from "react-icons/tb";

import Tab, { TabProps } from "../../../molecules/Tab";
import PageForm from "../../../templates/PageForm";
import MiniLoading from "../../../ions/MiniLoading";
import ErrorMessage from "../../../atoms/ErrorMessage";
import LegalPersonFormTab1 from "./LegalPersonFormTab1";
import LegalPersonRegistrationNotification from "./LegalPersonRegistrationNotification";
import PersonFormBankAccount from "../shared/PersonFormBankAccount";
import PersonFormAddresses from "../shared/PersonFormAddresses";
import PersonFormContacts from "../shared/PersonFormContacts";
import PersonFormDocuments from "../shared/PersonFormDocuments";
import GuarantorRetailFormTab from "./GuarantorRetailFormTab";
import LegalPersonSignatoriesTab from "./LegalPersonSignatoriesTab";

import useFetch from "../../../../hooks/useFetch";
import useCities from "../../../../hooks/useCities";
import useProvinces from "../../../../hooks/useProvinces";
import { useProfileLoader } from "../../../../hooks/useProfileLoader";
import { ProfileStorageManager } from "../../../../utils/profileStorageManager";

import { ErrorField } from "../../../../types/ErrorField";
import { storage } from "../../../../utils/storage";
import { removeErrorField } from "../../../../utils/formErrorUtils";
import appConfig from "../../../../config/app-config";
import { buildGuarantorRetailProfilesRequest, buildNewProfilesRequest, personApi } from "../../../../services/personApi";
import { missingBasicDataFields, missingGuarantorRetailFields } from "./legalPersonValidation";
import { BasicDataPayload, buildBasicDataPayload, canNotifyRegistrationUpdated, hasUnsavedChanges } from "./registrationNotification";

// Função para obter token
const getToken = async () => {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
};

const fetchBankAccountsFromServer = async (personId: string): Promise<any[] | null> => {
    const token = await getToken();
    const response = await fetch(`${BASE_URL}/${personId}/page-form`, {
        headers: {
            'x-api-key': PERSON_API_KEY || '',
            'Authorization': `Bearer ${token}`,
        },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.bankAccounts || [];
};

// ✅ Cache para evitar logs excessivos
let lastProfilesCache = '';

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;
const { address: GEOLOCATION_API_URL, key: GEOLOCATION_API_KEY } = appConfig.apis.geolocationApiEnv;

const BASE_URL = `${PERSON_API_URL}/legal-persons`;

const ProfilesMap: Record<string, string> = {
    'GUARANTOR-RETAIL': 'Aval. varejo',
    'GUARANTOR-WHOLESALE': 'Aval. atacado',
    'RECIPIENT': 'Recebedora',
    'ISSUER': 'Emitente',
    'DRAWEE': 'Sacado',
    'INTERNAL_CREDITOR': 'Credor interno',
    'EXTERNAL_CREDITOR': 'Credor externo',
};

const PROFILE_TO_TAB_MAPPING: Record<string, TabProps> = {
    'RECIPIENT': {
        id: 'tab-5',
        title: 'Contas bancárias',
        index: 4,
        icon: LuBadgeDollarSign,
    },
    'ISSUER': {
        id: 'tab-4',
        title: 'Emitente PJ',
        index: 5,
        icon: BsBuilding,
    },
    'GUARANTOR-WHOLESALE': {
        id: 'tab-3',
        title: 'Aval. atacado',
        index: 3,
        icon: TbLetterA,
    },
    'GUARANTOR-RETAIL': {
        id: 'tab-2',
        title: 'Aval. varejo',
        index: 2,
        icon: TbLetterV,
    },
    // ✅ NOVAS ABAS ALL (sempre visíveis)
    'ALL-SIGNATORIES': {
        id: 'tab-signatories',
        title: 'Signatários',
        index: 1,
        icon: TbWritingSign,
    },
    'ALL-ADDRESSES': {
        id: 'tab-6',
        title: 'Endereços',
        index: 6,
        icon: TbMapPin,
    },
    'ALL-CONTACTS': {
        id: 'tab-7',
        title: 'Contatos',
        index: 7,
        icon: TbPhone,
    },
    'ALL-DOCUMENTS': {
        id: 'tab-8',
        title: 'Documentos',
        index: 8,
        icon: TbFileText,
    },
};

type Form = {
    id: string;
    personId: string;
    personUuid?: string;  // UUID real do person
    govTaxId: string;
    fantasyName: string;
    legalName: string;
    zipcode: string;
    street: string;
    streetType: string;
    number: string;
    complement: string;
    district: string;
    cityId: string;
    province: string;
    bankAccounts: any[];
    logo?: File | null | string;
    logoUrl?: string;
    alias?: string;
    score?: number;
    limit?: number;
    guaranteePercentage?: number;
    registrationStatus?: string;
}

type MissingFieldsWarningProps = {
    fields: string[];
    tabLabel: string;
    hint: string;
};

const MissingFieldsWarning = ({ fields, tabLabel, hint }: MissingFieldsWarningProps) => {
    if (fields.length === 0) return null;
    return (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-slate-800 dark:border-yellow-700">
            <p className="text-sm font-medium text-yellow-800 mb-2 dark:text-yellow-300">⚠️ Campos obrigatórios não preenchidos:</p>
            <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1 dark:text-yellow-300">
                {fields.map((field) => (
                    <li key={field}>{field} <span className="text-xs italic">(aba: {tabLabel})</span></li>
                ))}
            </ul>
            <p className="text-xs text-yellow-600 mt-2 dark:text-yellow-400">{hint}</p>
        </div>
    );
};

const LegalPersonPageEdit = () => {

    const { fetchCities } = useCities();
    const { fetchProvinces } = useProvinces();
    const isErrorOnLoadingGuarantorRetail = useSignal<boolean>(false);
    const isLoadingGuarantorRetail = useSignal<boolean>(false);

    const { personId = "" } = useParams();

    const navigate = useNavigate();
    const isSendingForm = useSignal<boolean>(false);
    const savedBasicData = useSignal<BasicDataPayload | null>(null);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = useSignal('');
    const isLoadingForm = useSignal<boolean>(false);
    const provinces = useSignal<any[]>([]);
    const cities = useSignal<any[]>([]);
    const banks = useSignal<any[]>([]);

    // ✅ Estado para profiles do banco
    const [dbProfiles, setDbProfiles] = useState<string[]>([]);

    const form = useSignal<Form>({
        id: '',
        personId: '',
        govTaxId: '',
        fantasyName: '',
        legalName: '',
        zipcode: '',
        street: '',
        streetType: '',
        number: '',
        complement: '',
        district: '',
        cityId: '',
        province: '',
        bankAccounts: [],
        guaranteePercentage: 100,
        registrationStatus: 'IN_NEGOTIATION',
    });

    const tabItems = useSignal<TabProps[]>([
        { id: "tab-1", title: 'Dados cadastrais', index: 0, canEdit: true, icon: BsBuilding },
    ]);

    const [searchParams] = useSearchParams();
    const tabIndex: number = parseInt(searchParams.get('tab') || "0");

    const cancelForm = () => {
        // Limpar localStorage ao cancelar
        ProfileStorageManager.clear('legal_person', personId);
        console.log('🗑️ Cancelamento - localStorage limpo para:', personId);

        const listState = storage.getLegalPersonListState();
        const filters = storage.getLegalPersonFilters();
        const { page, pageSize, field, order } = listState;
        const { txtPartnerName, txtGovTaxId } = filters;

        const encodedGovTaxId = encodeURIComponent(txtGovTaxId);
        const url = `/person/partners/${page}/${pageSize}/${field}/${order}/p=${txtPartnerName};r=${encodedGovTaxId};`;
        navigate(url);
    };

    const { isLoading, data, error, isTokenError } = useFetch(`${BASE_URL}/${personId}/page-form`, [{ key: 'x-api-key', value: PERSON_API_KEY }]);

    const handleBankAccountChange = async () => {
        const bankAccounts = await fetchBankAccountsFromServer(personId);
        if (bankAccounts === null) {
            toast.error("Erro ao atualizar as contas bancárias!");
            return;
        }
        form.value = { ...form.value, bankAccounts };
    };

    // ✅ CORREÇÃO: loadFromDatabase APÓS useFetch para evitar erro de inicialização
    const loadFromDatabase = useCallback(async () => {
        if (data?.profiles) {
            const profiles = data.profiles.map((p: any) => p.type);
            console.log('🔍 loadFromDatabase - profiles do banco:', profiles);
            return profiles;
        }
        return [];
    }, [data]);

    // ✅ Inicializar useProfileLoader
    const {
        profiles: loadedProfiles,
        saveProfiles
    } = useProfileLoader(
        'legal_person',
        personId,
        loadFromDatabase
    );

    const profilesArray = useSignal<string[]>(loadedProfiles || []);

    // ✅ Sincronizar profiles carregados e adicionar abas
    useEffect(() => {
        console.log('🔍 DEBUG useEffect - loadedProfiles:', loadedProfiles);
        console.log('🔍 DEBUG useEffect - profilesArray.value:', profilesArray.value);

        if (loadedProfiles.length > 0) {
            profilesArray.value = loadedProfiles;
            alertTrigger.value++;
            console.log(`🔄 Profiles sincronizados: ${loadedProfiles.join(', ')}`);

            // Adicionar abas baseado em profiles carregados
            loadedProfiles.forEach((profile: string) => {
                const tabItem = PROFILE_TO_TAB_MAPPING[profile];
                if (tabItem && !tabItems.value.find(item => item.id === tabItem.id)) {
                    tabItems.value.push(tabItem);
                }
            });
        }

        // ✅ SEMPRE adicionar abas ALL (igual ao Individual)
        const allTabs = ['ALL-SIGNATORIES', 'ALL-ADDRESSES', 'ALL-CONTACTS', 'ALL-DOCUMENTS'];
        allTabs.forEach((tabKey) => {
            const tabItem = PROFILE_TO_TAB_MAPPING[tabKey];
            if (tabItem && !tabItems.value.some(item => item.id === tabItem.id)) {
                tabItems.value.push(tabItem);
                console.log(`✅ Aba ALL adicionada: ${tabItem.title}`);
            }
        });
    }, [loadedProfiles]);

    useEffect(() => {
        loadProvinces();
    }, []);

    const loadProvinces = async () => {
        isLoadingForm.value = true;
        try {
            provinces.value = await fetchProvinces();
        } catch (error) {
            console.error('Erro ao carregar os estados:', error);
            toast.error("Erro ao carregar os estados.");
        }
        isLoadingForm.value = false;
    };

    const fetchCitiesWrapper = async (province: string) => {
        try {
            isLoadingForm.value = true;
            cities.value = await fetchCities(province);
        } catch (error) {
            toast.error("Erro ao carregar as cidades. Tente novamente!");
        } finally {
            isLoadingForm.value = false;
        }
    };

    const getCity = async (cityId: number) => {
        isLoadingForm.value = true;
        try {
            const response = await fetch(`${GEOLOCATION_API_URL}/cities/${cityId}?fields=id,name,province.initials`, {
                headers: {
                    "x-api-key": GEOLOCATION_API_KEY,
                },
            });
            isLoadingForm.value = false;
            return await response.json();
        } catch (error) {
            toast.error("Erro ao carregar a cidade. Tente novamente!");
        }
        isLoadingForm.value = false;
    };



    const saveBasicData = async () => {
        const payload = buildBasicDataPayload(form.value);
        const response = await fetch(`${PERSON_API_URL}/legal-persons/${form.value.personId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': PERSON_API_KEY,
                'Authorization': `Bearer ${await getToken()}`
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            let errorMessage = `Erro ao salvar dados básicos (${response.status})`;
            try {
                const responseData = await response.json();
                errorMessage = responseData.error || responseData.message || errorMessage;
            } catch (parseError) {
                // response body não é JSON
            }
            throw new Error(errorMessage);
        }
        savedBasicData.value = payload;
    };

    const handleSaveProfiles = async () => {
        if (isSendingForm.value) return;

        const missing = missingRequiredFields.value;
        if (missing.length > 0) {
            toast.error(`Campos obrigatórios não preenchidos: ${missing.join(', ')}`, { autoClose: 8000 });
            return;
        }

        isSendingForm.value = true;
        try {
            saveProfiles(profilesArray.value);

            const request = buildNewProfilesRequest(profilesArray.value, dbProfiles);
            if (request.profiles.length > 0) {
                await personApi.saveLegalPersonProfiles(personId, request);
                setDbProfiles(Array.from(new Set([...dbProfiles, ...request.profiles.map(profile => profile.profile_type)])));
            }

            await saveBasicData();
            toast.success('Dados salvos com sucesso!');
        } catch (error: any) {
            console.error('Erro ao salvar:', error);
            toast.error(error.message || 'Erro ao salvar dados', { autoClose: 8000 });
        } finally {
            isSendingForm.value = false;
        }
    };

    // ✅ REPLICADO DO INDIVIDUAL: generateProfileList como computed
    const generateProfileList = computed(() => {
        // 🚨 FIX: Evitar re-renderização se profiles estão vazios
        if (!profilesArray.value || profilesArray.value.length === 0) {
            return <div>Nenhum perfil associado</div>;
        }

        // ✅ FIX: Usar variável externa para cache ao invés de propriedade do signal
        const profilesStr = JSON.stringify(profilesArray.value);
        if (lastProfilesCache !== profilesStr) {
            console.log('🔄 generateProfileList PJ executado:', profilesArray.value);
            lastProfilesCache = profilesStr;
        }

        return (
            <div className="mb-6 flex flex-wrap gap-2 px-4">
                {profilesArray.value.map((profile: string) => {
                    const isNew = !dbProfiles.includes(profile);
                    return (
                        <span key={profile} className={`
                            inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium
                            ${isNew
                                ? 'bg-gradient-to-r from-blue-500/20 to-indigo-500/20 backdrop-blur-sm border border-blue-300 shadow-sm hover:shadow-md transition-shadow'
                                : 'bg-white/60 backdrop-blur-sm border border-gray-200 shadow-sm'
                            }
                        `}>
                            {isNew ? (
                                <TbStar size={16} className="text-blue-600" />
                            ) : (
                                <TbCheck size={16} className="text-green-600" />
                            )}
                            <span className="font-medium text-sm">
                                {ProfilesMap[profile] || profile}
                            </span>
                        </span>
                    );
                })}
            </div>
        );
    });

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            // ✅ Extrair profiles do banco quando data carregar (igual ao Individual)
            if (data.profiles) {
                const profiles = data.profiles.map((p: any) => p.type);
                console.log('🔍 dbProfiles extraídos do useFetch:', profiles);
                setDbProfiles(profiles); // Agora extrai .type corretamente
            }

            // Extrair guarantor_data diretamente do response
            const guarantorProfile = data.profiles?.find((p: any) => p.type === 'GUARANTOR-RETAIL');
            const gData = guarantorProfile?.profile_dict?.guarantor_data;
            console.log('🔍 Dados extraídos:', { guarantorProfile, gData });

            form.value = {
                ...form.value,
                id: data.id,
                personId: data.personId,
                govTaxId: data.govTaxId,
                fantasyName: data.fantasyName,
                legalName: data.legalName,
                registrationStatus: data.registrationStatus || 'IN_NEGOTIATION',
                zipcode: data.zipcode,
                street: data.street,
                streetType: data.streetType,
                number: data.number,
                complement: data.complement,
                district: data.district,
                cityId: data.cityId,
                bankAccounts: data.bankAccounts,
                // Preencher campos do guarantor se existirem
                ...(gData && {
                    alias: gData.alias,
                    guaranteePercentage: gData.guarantee_percentage,
                    score: parseInt(gData.score) || 0,
                    limit: parseInt(gData.credit_limit) || 0,
                    logo: gData.logo_url || null,
                    logoUrl: gData.logo_url || ''
                })
            };
            savedBasicData.value = buildBasicDataPayload(form.value);
            banks.value = data.banks;

            // ✅ Só buscar cidade se cityId existir
            if (data.cityId) {
                getCity(data.cityId).then(city => {
                    if (city) {
                        const provinceInitials = city["province.initials"] || city?.province?.initials;
                        if (provinceInitials) {
                            form.value = {
                                ...form.value,
                                province: provinceInitials,
                            };
                            fetchCitiesWrapper(provinceInitials);
                        }
                    }
                });
            }

            if (data.profiles) {
                data.profiles.forEach((profile: any) => {
                    const tabItem = PROFILE_TO_TAB_MAPPING[profile.type];
                    if (tabItem && !tabItems.value.find(item => item.id === tabItem.id)) {
                        tabItems.value.push({
                            ...tabItem,
                            canEdit: false,
                        });
                    }
                });
            }
        }
    }, [data]);

    const setTabIndex = (tabIndex: number) => {
        navigate(`?tab=${tabIndex}`);
    };

    const alertTrigger = useSignal(0);

    const missingRequiredFields = computed(() => {
        void alertTrigger.value; // Forçar re-avaliação
        return missingBasicDataFields(form.value);
    });

    const missingGuarantorRetailTabFields = computed(() => {
        void alertTrigger.value;
        return missingGuarantorRetailFields(form.value, profilesArray.value);
    });

    const missingGuarantorFields = computed(() => {
        return [...missingRequiredFields.value, ...missingGuarantorRetailTabFields.value];
    });

    const updateButtonDisable = computed(() => {
        return missingRequiredFields.value.length > 0 || isLoading || isSendingForm.value;
    });

    const changeForm = (field: string, value: string | File | null) => {
        form.value = {
            ...form.value,
            [field]: value,
        };
        removeErrorField(errorFields.value, field);
    };

    const notifyState = canNotifyRegistrationUpdated(
        savedBasicData.value?.registrationStatus,
        hasUnsavedChanges(savedBasicData.value, form.value, profilesArray.value, dbProfiles),
    );

    const newBankAccount = () => {
        // criar evento
        window.dispatchEvent(new CustomEvent("BankAccountFormTab:openModal", {}));
    };

    const saveGuarantorProfiles = async () => {
        try {
            return await personApi.saveLegalPersonProfiles(personId, buildGuarantorRetailProfilesRequest(form.value));
        } catch (error) {
            console.error('Error saving profiles:', error);
            throw error;
        }
    };

    const submitGuarantorRetailForm = async () => {
        const missing = missingGuarantorFields.value;
        if (missing.length > 0) {
            toast.error(`Campos obrigatórios não preenchidos: ${missing.join(', ')}`, { autoClose: 8000 });
            return;
        }

        isSendingForm.value = true;

        try {
            // 0. Upload logo if a new file was selected
            if (form.value.logo instanceof File) {
                try {
                    const session = await Auth.currentSession();
                    const token = session.getIdToken().getJwtToken();
                    const reader = new FileReader();
                    const base64 = await new Promise<string>((resolve) => {
                        reader.onload = () => resolve((reader.result as string).split(',')[1]);
                        reader.readAsDataURL(form.value.logo as File);
                    });
                    const logoRes = await fetch(`${BASE_URL}/${personId}/logo`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'x-api-key': PERSON_API_KEY },
                        body: JSON.stringify({ file: base64, fileName: (form.value.logo as File).name }),
                    });
                    if (logoRes.ok) {
                        const logoData = await logoRes.json();
                        form.value = { ...form.value, logoUrl: logoData.logoUrl || '' };
                    }
                } catch (e) { console.error('Erro ao subir logo:', e); }
            }

            // 1. Salvar profiles via Person API (PRIMEIRO - independente)
            let profileSaved = false;
            try {
                await saveGuarantorProfiles();
                profileSaved = true;
                console.log('✅ Profile salvo com sucesso');
            } catch (profileError) {
                console.error('❌ Erro ao salvar profile:', profileError);
            }

            // 2. Salvar dados básicos via Person API
            let basicDataSaved = false;
            try {
                await saveBasicData();
                basicDataSaved = true;
                console.log('✅ Dados básicos salvos com sucesso');
            } catch (basicDataError: any) {
                console.error('❌ Erro ao salvar dados básicos:', basicDataError);
                toast.error(basicDataError.message || 'Erro ao salvar dados básicos', { autoClose: 8000 });
            }

            // 3. Resultado final
            if (profileSaved && basicDataSaved) {
                toast.success('Dados salvos com sucesso!');
            } else if (profileSaved) {
                toast.success('Perfis salvos com sucesso!');
            } else if (basicDataSaved) {
                toast.success('Dados básicos salvos com sucesso!');
            } else {
                toast.error('Erro ao salvar dados');
            }

            errorFields.value = [];
        } catch (error) {
            toast.error("Ocorreu um erro na comunicação com o servidor!");
        } finally {
            isSendingForm.value = false;
        }
    };

    return (
        <PageForm
            pageTitle={`#${form.value.id} - ${form.value.fantasyName}`}
            apiVersion={apiVersion.value}
            isLoading={isLoading || isSendingForm.value || isLoadingForm.value}
            breadcrumb={[
                {
                    label: "Listagem de parceiras",
                    url: (() => {
                        const listState = storage.getLegalPersonListState();
                        const { page, pageSize, field, order } = listState;
                        return `/person/partners/${page}/${pageSize}/${field}/${order}/`;
                    })()
                },
                { label: `#${form.value.id} - ${form.value.fantasyName}`, url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">

                    <button
                        id="btn-cancel-edit"
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded'
                        onClick={cancelForm}
                        title="Cancelar registro"
                        disabled={isLoading || isSendingForm.value}
                    >
                        Cancelar
                    </button>

                    {tabIndex === 0 &&
                        <button
                            id="btn-update-legal-person"
                            className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={handleSaveProfiles}
                            title="Atualizar registro"
                            disabled={updateButtonDisable.value}

                        >
                            Atualizar
                        </button>
                    }

                    {tabIndex === 2 &&
                        <button
                            id="btn-save-guarantor"
                            className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={submitGuarantorRetailForm}
                            title="Salvar registro"
                            disabled={isLoading || isSendingForm.value}
                        >
                            Salvar
                        </button>
                    }

                    {tabIndex === 4 &&
                        <button
                            id="btn-add-bank-account"
                            className="flex items-center bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={newBankAccount}
                            title="Cadastrar nova conta bancária"
                        >
                            <BsBank className="mr-2" />
                            Nova conta
                        </button>
                    }
                </div>
            )}
        >
            <Fragment>
                {/* ✅ Badges de Profiles - USANDO generateProfileList computed (igual ao Individual) */}
                {generateProfileList.value}

                <Tab tabItems={tabItems.value} tabIndex={tabIndex} setTabIndex={setTabIndex}></Tab>
                <div id="tab-content" className="overflow-y-auto">
                    {tabIndex === 0 &&
                        <LegalPersonFormTab1
                            key="tab1"
                            form={form.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            provinces={provinces.value}
                            cities={cities.value}
                            changeProvince={fetchCitiesWrapper}
                            editForm={true}
                            statusAction={
                                <LegalPersonRegistrationNotification
                                    personId={personId}
                                    legalName={savedBasicData.value?.legalName || ''}
                                    govTaxId={savedBasicData.value?.govTaxId || ''}
                                    enabled={notifyState.enabled}
                                    reason={notifyState.reason}
                                />
                            }
                        />
                    }

                    {tabIndex === 1 &&
                        <LegalPersonSignatoriesTab legalPersonUuid={personId} requiresSignatoryVerification={data?.requiresSignatoryVerification} onVerificationToggle={(enabled) => { if (data) data.requiresSignatoryVerification = enabled; }} />
                    }

                    {tabIndex === 2 &&
                        <div className="p-4 m-4"
                            id="retail-guarantor">
                            {isLoadingGuarantorRetail.value ?
                                <p><MiniLoading /> Carregando dados do avalista varejo...</p>
                                : <>
                                    {
                                        isErrorOnLoadingGuarantorRetail.value ?
                                            <ErrorMessage message="Erro ao carregar os dados do avalista varejo. Volte para a listagem e tente novamente!" />
                                            :
                                            <GuarantorRetailFormTab
                                                form={form.value}
                                                errorFields={errorFields.value}
                                                changeForm={changeForm}
                                                editForm={tabItems.value.find(item => item.id === 'tab-2')?.canEdit || true}
                                            />
                                    }
                                </>
                            }
                        </div>}


                    {tabIndex === 3 &&
                        <div className="p-4 m-4"
                            id="wholesale-guarantor">
                            Avalista atacado

                        </div>}

                    {tabIndex === 4 &&
                        <PersonFormBankAccount
                            key="tab3"
                            personId={personId}
                            personType="legal_person"
                            provinces={provinces.value}
                            banks={banks.value}
                            bankAccounts={form.value.bankAccounts}
                            onBankAccountChange={handleBankAccountChange}
                        />}

                    {tabIndex === 5 &&
                        <div className="p-4 m-4"
                            id="issuer">
                            Emitente PJ
                        </div>}

                    {/* ✅ NOVAS ABAS com componentes genéricos */}
                    {tabIndex === 6 && (
                        <PersonFormAddresses
                            personId={personId}
                            personType="legal_person"
                        />
                    )}

                    {tabIndex === 7 && (
                        <PersonFormContacts
                            personId={personId}
                            personType="legal_person"
                        />
                    )}

                    {tabIndex === 8 && (
                        <PersonFormDocuments
                            personId={personId}
                            personType="legal_person"
                        />
                    )}
                </div>
            </Fragment>

            {tabIndex === 0 &&
                <MissingFieldsWarning
                    fields={missingRequiredFields.value}
                    tabLabel="Dados cadastrais"
                    hint="Preencha estes campos na aba correspondente para habilitar o botão Atualizar."
                />
            }

            {tabIndex === 2 &&
                <MissingFieldsWarning
                    fields={missingGuarantorRetailTabFields.value}
                    tabLabel="Aval. varejo"
                    hint="Preencha estes campos antes de clicar em Salvar."
                />
            }

        </PageForm>
    );
};


export default LegalPersonPageEdit;